import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { FieldAttributes, FieldConfig, useField } from "formik";
import React, { useId } from "react";

interface Props extends FieldConfig, FieldAttributes<unknown> {
  label: string;
  isRequired?: boolean;
  isLoaded?: boolean;
  withInfo?: React.ReactNode;
  placeholder?: string;
  value?: unknown;
  // Callers pass through arbitrary input attributes and legacy style props
  // (`id`, `mb`, `readOnly`, ...), which `FieldAttributes` used to absorb.
  [attribute: string]: unknown;
}

const RequiredMark = () => (
  <span className="text-danger" aria-hidden>
    *
  </span>
);

export const CheckboxField = ({
  label,
  isRequired = false,
  isLoaded = true,
  withInfo,
  placeholder,
  value,
  ...props
}: Props) => {
  const [field, meta, helpers] = useField(props.name);
  const id = useId();
  const isInvalid = meta.touched && !!meta.error;

  // Convert string boolean to actual boolean
  const isChecked =
    typeof field.value === "string"
      ? field.value.toLowerCase() === "true"
      : Boolean(field.value);

  const handleChange = (checked: boolean) => {
    // Update the field value as a string to match the expected format
    helpers.setValue(checked ? "true" : "false");
  };

  return (
    <div className="flex items-start gap-2">
      <Checkbox
        id={id}
        name={field.name}
        checked={isChecked}
        onCheckedChange={(checked) => handleChange(checked === true)}
        onBlur={field.onBlur}
        required={isRequired}
        aria-invalid={isInvalid || undefined}
        className="mt-px"
      />
      <Label
        htmlFor={id}
        className="text-base leading-snug font-normal whitespace-normal text-fg"
      >
        {label} {withInfo}
        {isRequired ? <RequiredMark /> : null}
      </Label>
    </div>
  );
};

export const InputField = ({
  label,
  isRequired = false,
  isLoaded = true,
  withInfo,
  placeholder,
  ...props
}: Props) => {
  const [field, meta] = useField(props.name);
  const id = useId();
  const errorId = `${id}-error`;
  const isInvalid = meta.touched && !!meta.error;

  return (
    <div className="flex w-full flex-col gap-1.5">
      <Label
        htmlFor={id}
        className="leading-snug font-normal break-words whitespace-normal text-fg-2"
      >
        {label} {withInfo}
        {isRequired ? <RequiredMark /> : null}
      </Label>
      {isLoaded ? (
        <Input
          {...field}
          id={id}
          type={typeof props.type === "string" ? props.type : undefined}
          placeholder={placeholder}
          required={isRequired}
          aria-invalid={isInvalid || undefined}
          aria-describedby={isInvalid ? errorId : undefined}
        />
      ) : (
        <Skeleton className="h-7 w-full" />
      )}
      {isInvalid ? (
        <p id={errorId} className="text-sm text-danger">
          {meta.error}
        </p>
      ) : null}
    </div>
  );
};
